import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { AuthService } from './auth.service';

/** Allowed `redirect` query values after token handoff. */
const HANDOFF_REDIRECTS = new Set(['wip', 'completed', 'admin']);

/**
 * Reads tenant + URL-encoded JWT from `/:tenantId/handoff/:token/wip`, persists auth, then
 * navigates to the real WIP route with replaceUrl (token leaves the address bar).
 */
export const wipHandoffGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const tenantId = route.paramMap.get('tenantId')?.trim() ?? '';
  const rawToken = route.paramMap.get('token') ?? '';

  let token: string;
  try {
    token = decodeURIComponent(rawToken);
  } catch {
    return new RedirectCommand(
      router.createUrlTree(['/login'], {
        queryParams: { handoffError: 'decode', returnUrl: state.url },
      }),
      { replaceUrl: true },
    );
  }

  if (!tenantId || !token.trim()) {
    return new RedirectCommand(
      router.createUrlTree(['/login'], {
        queryParams: { handoffError: 'missing' },
      }),
      { replaceUrl: true },
    );
  }

  auth.applyHandoffFromUrl(tenantId, token.trim());

  const redirectRaw = route.queryParamMap.get('redirect')?.toLowerCase() ?? 'wip';
  const redirect = HANDOFF_REDIRECTS.has(redirectRaw) ? redirectRaw : 'wip';

  const commands =
    redirect === 'completed'
      ? ['/', tenantId, 'wip', 'completed']
      : redirect === 'admin'
        ? ['/', tenantId, 'admin']
        : ['/', tenantId, 'wip'];

  return new RedirectCommand(router.createUrlTree(commands), { replaceUrl: true });
};
